import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import * as Api from "../utils/Api";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

const SewContext = createContext();

export const SewProvider = ({ children }) => {
    const [sewAssets, setSewAssets] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const hasFetchedRef = useRef(false);

    /**
     * Fetches all SEW systems (blue assets) and stores them keyed by Id.
     *
     * The /battle-loop/blue-assets endpoint returns the full list in a single
     * call, so the result is cached after the first successful fetch. Pass
     * `override = true` to force a refresh.
     *
     * @param {boolean} [override=false] - Whether to bypass the cached result.
     * @returns {Promise<Object>} A promise resolving to assets keyed by Id.
     */
    const fetchSewAssets = useCallback(async (override = false) => {
        if (hasFetchedRef.current && !override) {
            return sewAssets;
        }

        setIsLoading(true);
        try {
            const response = await Api.getBlueAssets();
            const list = Array.isArray(response) ? response : (response?.data ?? []);

            const assetMap = {};
            for (const asset of list) {
                if (asset && asset.Id != null) {
                    assetMap[asset.Id] = asset;
                }
            }

            setSewAssets(assetMap);
            hasFetchedRef.current = true;
            return assetMap;
        } catch (error) {
            toast.error(`Error fetching SEW systems: ${error.message}`);
            return {};
        } finally {
            setIsLoading(false);
        }
    }, [sewAssets]);

    useEffect(() => {
        fetchSewAssets();
    }, [fetchSewAssets]);

    const contextValue = { sewAssets, isLoading, fetchSewAssets };

    return (
        <SewContext.Provider value={contextValue}>
            {children}
        </SewContext.Provider>
    );
};

SewProvider.propTypes = {
    children: PropTypes.node.isRequired
};

// Custom hook to use the SewContext
export const useSew = () => {
    return useContext(SewContext);
};
